import type { IPropertyBag } from '@deity-io/di-data-basics';
import { Icon, Modal } from '@deity-io/portal-ui';
import { EntityNameDescriptionFields } from 'components/FormFields';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  GetPackageVersionContentInfoDocument,
  useCreateModelMutation,
  useUpdateModelMutation
} from 'services/graphql/generated/hooks';
import type { Maybe } from 'services/graphql/generated/schemas';
import { v4 } from 'uuid';
import * as Yup from 'yup';

interface CreateEditStructureModalProps {
  isOpen?: boolean;
  isEdit?: boolean;
  versionUuid: string;
  editModel?:
    | {
        __typename?: 'EntityType' | undefined;
        uuid: string;
        schema: any;
        name: string;
        description?: Maybe<string> | undefined;
      }
    | undefined;
  setOpenState: (state: boolean) => void;
  onPackageCreated: () => void;
  schema?: IPropertyBag;
}

export interface CreateEditStructureInput {
  name: string;
  description: string;
}

export const CreateStructureInputSchema = Yup.object().shape({
  name: Yup.string().min(1, 'Too short').max(50, 'Too long').required('This is a required field.'),
  description: Yup.string().min(2, 'Too short').max(50, 'Too long')
});

export const CreateEditStructureModal: FC<CreateEditStructureModalProps> = ({
  isOpen = false,
  setOpenState,
  onPackageCreated,
  isEdit,
  editModel,
  versionUuid,
  schema
}) => {
  const refetchQueries = {
    refetchQueries: [
      {
        query: GetPackageVersionContentInfoDocument,
        variables: {
          versionUuid
        }
      }
    ]
  };
  const [create, { data: createData, loading: createLoading }] = useCreateModelMutation(refetchQueries);
  const [update, { data: updateData, loading: updateLoading }] = useUpdateModelMutation(refetchQueries);

  useEffect(() => {
    if (createData || updateData) {
      onPackageCreated();
      setOpenState(false);
    }
  }, [createData, updateData]);

  const createOrUpdate = (input: CreateEditStructureInput) => {
    if (!input.description) {
      input.description = '';
    }

    return isEdit ? updateModel(input) : createModel(input);
  };

  const createModel = (input: CreateEditStructureInput) => {
    create({
      variables: {
        uuid: v4(),
        packageVersionUuid: versionUuid,
        name: input.name,
        description: input.description,
        schema: schema || {
          _meta: { type: 'dict' }
        }
      }
    }).then();
  };

  const updateModel = (input: CreateEditStructureInput) => {
    update({
      variables: {
        packageVersionUuid: versionUuid,
        uuid: editModel?.uuid ?? '',
        name: input.name,
        description: input.description
      }
    }).then();
  };

  const formik = useFormik({
    initialValues: {
      name: editModel?.name ?? '',
      description: editModel?.description ?? ''
    },
    validateOnBlur: true,
    validationSchema: CreateStructureInputSchema,
    onSubmit: values => createOrUpdate(values)
  });

  useEffect(() => {
    if (editModel) {
      const { name, description } = editModel;
      formik.setValues({ name: name!, description: description! });
    }
  }, [editModel]);

  const confirmText = () => {
    if (!isEdit) {
      return `${createLoading ? 'Creating' : 'Create'} Structure`;
    }

    return `${updateLoading ? 'Updating' : 'Update'} Structure`;
  };

  if (isEdit && !editModel) {
    return null!;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpenState(false)}
      label={`${isEdit ? 'Edit' : 'New'} Structure`}
      title="Enter a name and description."
      closeIcon={<Icon icon="close" />}
      confirmText={confirmText()}
      onConfirm={() => formik.handleSubmit()}
    >
      <form onSubmit={formik.handleSubmit}>
        <EntityNameDescriptionFields
          errors={formik.errors}
          values={formik.values}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
          entity="Structure"
        />
      </form>
    </Modal>
  );
};
